import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';

// A drop zone of the app cancels the drag itself, so a drag still uncancelled here would make the browser open the file in place of the app.
// A native file input accepts dropped files without cancelling anything, so it keeps its default.
const isUnhandledFileDrag = (event: DragEvent): boolean => {
    const isOverFileInput = event.target instanceof HTMLInputElement && event.target.type === 'file';
    return !event.defaultPrevented && !isOverFileInput && (event.dataTransfer?.types.includes('Files') ?? false);
};

const refuseUnhandledFileDrag = (event: DragEvent): void => {
    if (!isUnhandledFileDrag(event)) {
        return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'none';
    }
};

const cancelUnhandledFileDrop = (event: DragEvent): void => {
    if (isUnhandledFileDrag(event)) {
        event.preventDefault();
    }
};

@Injectable({ providedIn: 'root' })
export class UnhandledFileDropGuardService {
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _document = inject(DOCUMENT);
    private readonly _ngZone = inject(NgZone);

    // The window is the last stop of a bubbling drag event, so every drop zone of the app has accepted it or not by then.
    install(): void {
        const window = this._document.defaultView;
        if (!window) {
            return;
        }
        // Outside the zone, since dragover fires continuously and would run change detection each time.
        this._ngZone.runOutsideAngular(() => {
            window.addEventListener('dragover', refuseUnhandledFileDrag);
            window.addEventListener('drop', cancelUnhandledFileDrop);
        });
        this._destroyRef.onDestroy(() => {
            window.removeEventListener('dragover', refuseUnhandledFileDrag);
            window.removeEventListener('drop', cancelUnhandledFileDrop);
        });
    }
}
